import { Button } from '@/components/ui/Button'
import { RiskMeter } from '@/components/ui/RiskMeter'
import { riskClass } from '@/lib/format'
import type { Proposal } from '@/types/dashboard'

interface Props {
  proposal: Proposal
  focused: boolean
  onConfirm: (id: string) => void
  onDismiss: (id: string) => void
}

function pct(n?: number) {
  if (n == null || !Number.isFinite(n)) return null
  return `${Math.round(n * 100)}%`
}

export function ProposalRow({ proposal: x, focused, onConfirm, onDismiss }: Props) {
  const cal = x.calibration || x.orchestration?.calibration
  const cohort = x.orchestration?.cohort
  const trustLabel = cal
    ? cal.reliable
      ? `Trust ${pct(cal.calibrated)} · calibrated · n=${cal.sampleSize}`
      : `Trust ${pct(cal.calibrated)} · learning · n=${cal.sampleSize}`
    : null

  return (
    <article className={`row${focused ? ' focused' : ''}`} data-prop-id={x.id}>
      <div className="row-top">
        <div>
          <div className="row-title">{x.summary || x.intent || 'Proposal'}</div>
          <div className="row-meta">{x.notification || x.why || ''}</div>
        </div>
        <div className="pills">
          <span className={`pill ${riskClass(x.riskLevel)}`}>{x.riskLevel}</span>
          <span
            className={`pill ${x.status === 'draft' ? 'a' : x.status === 'dismissed' ? 'r' : 'g'}`}
          >
            {x.status}
          </span>
          {cal ? (
            <span className={`pill ${cal.reliable ? 'g' : 'a'}`} title={`raw ${pct(cal.raw)}`}>
              {pct(cal.calibrated)} trust
            </span>
          ) : null}
          {cohort ? (
            <span
              className="pill teal"
              title={`fingerprint ${cohort.patternFingerprint} · boost +${pct(cohort.confidenceBoost)}`}
            >
              cohort n={cohort.cohortCount}
            </span>
          ) : null}
          {x.verified ? <span className="pill teal">verified</span> : null}
          {x.requiresHumanReview ? <span className="pill a">review</span> : null}
        </div>
      </div>
      <RiskMeter level={x.riskLevel} />
      <div className="row-meta" style={{ marginTop: 8 }}>
        {x.category || '—'} · hash {(x.promptHash || '').slice(0, 10)} · {x.source || ''}
        {trustLabel ? ` · ${trustLabel}` : ''}
        {cal && cal.raw !== cal.calibrated
          ? ` · raw ${pct(cal.raw)}`
          : ''}
        {cohort ? ` · cohort +${pct(cohort.confidenceBoost)}` : ''}
      </div>
      {x.why ? (
        <details className="why">
          <summary>Why this suggestion</summary>
          <div style={{ marginTop: 6 }}>{x.why}</div>
        </details>
      ) : null}
      {x.status === 'draft' ? (
        <div className="row-actions">
          <Button variant="ok" size="sm" onClick={() => onConfirm(x.id)}>
            Confirm
          </Button>
          <Button variant="danger" size="sm" onClick={() => onDismiss(x.id)}>
            Dismiss
          </Button>
        </div>
      ) : null}
    </article>
  )
}
